import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCustomer } from '../../context/CustomerContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { 
  ArrowLeft, Package, Truck, MapPin, Clock, 
  CheckCircle, CreditCard, RotateCcw, Shield 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useCustomer();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/customer/orders/${orderId}`, {
        headers: getAuthHeaders()
      });
      setOrder(response.data.order);
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      return_requested: 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-stone-100 text-stone-800';
  };

  const canReturn = order && ['delivered', 'completed', 'shipped'].includes(order.order_status);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Package className="h-16 w-16 mx-auto mb-4 text-stone-300" />
          <h3 className="text-lg font-medium text-stone-800 mb-2">Order not found</h3>
          <Link to="/account/orders">
            <Button variant="link" className="text-amber-700">
              Back to Orders
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="order-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/account/orders')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-serif text-stone-800">{order.order_number}</h2>
            <p className="text-stone-500 text-sm flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Ordered on {new Date(order.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <Badge className={getStatusColor(order.order_status)}>
          {order.order_status?.replace('_', ' ')}
        </Badge>
      </div>

      {/* Tracking Info */}
      {order.tracking_number && (
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Truck className="h-6 w-6 text-purple-600" />
              <div>
                <p className="font-medium text-purple-800">Tracking Number</p>
                <p className="text-purple-600">{order.tracking_number}</p>
              </div>
            </div>
            {order.carrier && (
              <Badge variant="outline" className="border-purple-300 text-purple-700">
                {order.carrier}
              </Badge>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Order Items */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-serif">Order Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {order.items?.map((item, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg"
                >
                  <div className="w-16 h-16 bg-stone-200 rounded flex items-center justify-center">
                    <Package className="h-8 w-8 text-stone-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-stone-800">{item.product_name}</p>
                    <p className="text-sm text-stone-500">Qty: {item.quantity}</p>
                  </div>
                  <p className="font-semibold text-stone-800">
                    ${(item.price * item.quantity).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>

            <Separator className="my-6" />

            {/* Order Summary */}
            <div className="space-y-2">
              <div className="flex justify-between text-stone-600">
                <span>Subtotal</span>
                <span>${order.subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-stone-600">
                <span>Shipping</span>
                <span>${order.shipping?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between text-stone-600">
                <span>Tax</span>
                <span>${order.tax?.toFixed(2) || '0.00'}</span>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between text-lg font-bold text-stone-800">
                <span>Total</span>
                <span>${order.total?.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Shipping Address */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Shipping Address
              </CardTitle>
            </CardHeader>
            <CardContent>
              {order.shipping_address ? (
                <div className="text-sm text-stone-600 space-y-1">
                  <p>{order.shipping_address.street}</p>
                  <p>
                    {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}
                  </p>
                  <p>{order.shipping_address.country}</p>
                </div>
              ) : (
                <p className="text-sm text-stone-500">No address on file</p>
              )}
            </CardContent>
          </Card>

          {/* Payment */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Payment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="capitalize">
                  {order.payment_method || 'Card'}
                </Badge>
                <Badge className="bg-green-100 text-green-800">
                  {order.payment_status || 'Paid'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardContent className="p-4 space-y-3">
              {canReturn && (
                <Link to={`/account/returns/new?order=${order.id}`} className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Request Return
                  </Button>
                </Link>
              )}
              <Link to={`/account/warranty?order=${order.id}`} className="block">
                <Button variant="outline" className="w-full justify-start">
                  <Shield className="h-4 w-4 mr-2" />
                  Register Warranty
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AccountOrderDetail;
